import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "./icons";

/**
 * Step-by-step "add to home screen" instructions. Every platform hides the
 * install affordance somewhere different, and iOS never fires
 * `beforeinstallprompt` at all, so the only thing that actually helps is a
 * picture of the button people are meant to press.
 */

type PlatformId = "ios" | "android" | "desktop";

type Step = { text: string; figure: React.ReactNode };

/* --- figures ------------------------------------------------------------ */

/** Phone-shaped stage every figure draws inside, so the steps stay aligned. */
function Frame({ children, landscape = false }: { children: React.ReactNode; landscape?: boolean }) {
  const w = landscape ? 260 : 168;
  const h = landscape ? 168 : 260;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="guide__svg" role="presentation" aria-hidden="true">
      <rect x="1" y="1" width={w - 2} height={h - 2} rx={landscape ? 10 : 20} fill="var(--surface-muted)" stroke="var(--border-strong)" strokeWidth="2" />
      {children}
    </svg>
  );
}

/** Pulsing ring that points at the control the step is talking about. */
function Marker({ x, y, r = 16 }: { x: number; y: number; r?: number }) {
  return <circle cx={x} cy={y} r={r} className="guide__marker" fill="none" stroke="var(--brand)" strokeWidth="3" />;
}

function Row({ y, w = 96, muted = false }: { y: number; w?: number; muted?: boolean }) {
  return <rect x="20" y={y} width={w} height="8" rx="4" fill={muted ? "var(--border)" : "var(--border-strong)"} />;
}

const shareGlyph = (
  <g stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
    <path d="M84 218v-26" />
    <path d="M77 199l7-7 7 7" />
    <path d="M72 208h-8v22h40v-22h-8" />
  </g>
);

const dotsGlyph = (x: number, y: number) => (
  <g fill="var(--brand)">
    <circle cx={x} cy={y - 6} r="2.6" />
    <circle cx={x} cy={y} r="2.6" />
    <circle cx={x} cy={y + 6} r="2.6" />
  </g>
);

const iosSteps: Step[] = [
  {
    text: "เปิดหน้านี้ด้วย Safari — iOS ติดตั้งแอปได้จาก Safari เท่านั้น Chrome หรือ in-app browser ของ LINE/Facebook ทำไม่ได้",
    figure: (
      <Frame>
        <rect x="14" y="18" width="140" height="26" rx="13" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <text x="84" y="36" textAnchor="middle" fontSize="11" fill="var(--text-muted)">pwa-notification</text>
        <Row y={64} w={124} />
        <Row y={82} w={100} muted />
        <Row y={100} w={112} muted />
        <Marker x={84} y={31} r={22} />
      </Frame>
    ),
  },
  {
    text: "แตะปุ่ม Share (สี่เหลี่ยมมีลูกศรชี้ขึ้น) ที่แถบเครื่องมือด้านล่าง",
    figure: (
      <Frame>
        <Row y={40} w={124} muted />
        <Row y={58} w={96} muted />
        <Row y={76} w={112} muted />
        <rect x="8" y="186" width="152" height="60" rx="14" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        {shareGlyph}
        <circle cx="34" cy="216" r="8" fill="var(--border)" />
        <circle cx="134" cy="216" r="8" fill="var(--border)" />
        <Marker x={84} y={214} r={22} />
      </Frame>
    ),
  },
  {
    text: 'เลื่อนเมนูขึ้นแล้วเลือก "Add to Home Screen" (เพิ่มไปยังโฮมสกรีน)',
    figure: (
      <Frame>
        <rect x="8" y="70" width="152" height="176" rx="16" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <rect x="62" y="80" width="44" height="4" rx="2" fill="var(--border-strong)" />
        <Row y={104} w={80} muted />
        <Row y={128} w={92} muted />
        <rect x="14" y="146" width="140" height="30" rx="8" fill="var(--brand-soft)" />
        <text x="26" y="165" fontSize="10.5" fill="var(--brand-strong)">Add to Home Screen</text>
        <rect x="130" y="153" width="16" height="16" rx="4" fill="none" stroke="var(--brand)" strokeWidth="2" />
        <path d="M138 157v8M134 161h8" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" />
        <Row y={192} w={72} muted />
        <Marker x={84} y={161} r={26} />
      </Frame>
    ),
  },
  {
    text: 'แตะ "Add" มุมขวาบน แล้วเปิดแอปจากไอคอนบนหน้าจอโฮมทุกครั้ง — ต้องเปิดจากไอคอนเท่านั้น notification จึงจะทำงาน',
    figure: (
      <Frame>
        <rect x="52" y="60" width="64" height="64" rx="16" fill="var(--brand)" />
        <path d="M74 100v-18M68 88l6-6 6 6" stroke="var(--brand-ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <circle cx="98" cy="94" r="9" fill="var(--brand-ink)" opacity="0.9" />
        <text x="84" y="142" textAnchor="middle" fontSize="11" fill="var(--text-muted)">Push Demo</text>
        <Marker x={84} y={92} r={44} />
      </Frame>
    ),
  },
];

const androidSteps: Step[] = [
  {
    text: "เปิดหน้านี้ใน Chrome แล้วแตะปุ่ม ⋮ มุมขวาบน",
    figure: (
      <Frame>
        <rect x="14" y="18" width="140" height="26" rx="13" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <text x="70" y="36" textAnchor="middle" fontSize="11" fill="var(--text-muted)">pwa-notification</text>
        {dotsGlyph(140, 31)}
        <Row y={64} w={124} muted />
        <Row y={82} w={100} muted />
        <Marker x={140} y={31} r={16} />
      </Frame>
    ),
  },
  {
    text: 'เลือก "Install app" หรือ "เพิ่มลงในหน้าจอหลัก" (Add to Home screen)',
    figure: (
      <Frame>
        <rect x="62" y="20" width="98" height="150" rx="10" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <rect x="70" y="34" width="60" height="7" rx="3.5" fill="var(--border)" />
        <rect x="70" y="56" width="72" height="7" rx="3.5" fill="var(--border)" />
        <rect x="66" y="76" width="90" height="26" rx="7" fill="var(--brand-soft)" />
        <text x="74" y="93" fontSize="10.5" fill="var(--brand-strong)">Install app</text>
        <rect x="70" y="116" width="66" height="7" rx="3.5" fill="var(--border)" />
        <rect x="70" y="138" width="54" height="7" rx="3.5" fill="var(--border)" />
        <Marker x={111} y={89} r={22} />
      </Frame>
    ),
  },
  {
    text: 'กด "Install" ในกล่องยืนยัน แล้วเปิดแอปจากไอคอนบนหน้าจอโฮม',
    figure: (
      <Frame>
        <rect x="18" y="86" width="132" height="88" rx="14" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <rect x="30" y="100" width="26" height="26" rx="7" fill="var(--brand)" />
        <text x="64" y="118" fontSize="11" fill="var(--text)">Push Demo</text>
        <rect x="86" y="140" width="52" height="22" rx="11" fill="var(--brand)" />
        <text x="112" y="155" textAnchor="middle" fontSize="10.5" fill="var(--brand-ink)">Install</text>
        <Marker x={112} y={151} r={20} />
      </Frame>
    ),
  },
];

const desktopSteps: Step[] = [
  {
    text: "บน Chrome หรือ Edge ให้มองหาไอคอนติดตั้ง (จอมีลูกศรลง) ที่ท้ายช่อง address bar แล้วคลิก",
    figure: (
      <Frame landscape>
        <rect x="14" y="16" width="232" height="26" rx="8" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <text x="30" y="34" fontSize="11" fill="var(--text-muted)">pwa-notification</text>
        <rect x="206" y="22" width="16" height="13" rx="2" fill="none" stroke="var(--brand)" strokeWidth="2" />
        <path d="M214 25v6M211 28l3 3 3-3" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <Row y={64} w={180} muted />
        <Row y={84} w={150} muted />
        <Marker x={214} y={29} r={16} />
      </Frame>
    ),
  },
  {
    text: 'ไม่เห็นไอคอน? เปิดเมนู ⋮ มุมขวาบน แล้วเลือก "Install …" (Edge อยู่ใต้ Apps)',
    figure: (
      <Frame landscape>
        <rect x="14" y="16" width="232" height="26" rx="8" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        {dotsGlyph(232, 29)}
        <rect x="140" y="48" width="108" height="104" rx="10" fill="var(--surface)" stroke="var(--border)" strokeWidth="1.5" />
        <rect x="150" y="62" width="70" height="7" rx="3.5" fill="var(--border)" />
        <rect x="144" y="80" width="100" height="24" rx="7" fill="var(--brand-soft)" />
        <text x="152" y="96" fontSize="10.5" fill="var(--brand-strong)">Install Push Demo</text>
        <rect x="150" y="118" width="60" height="7" rx="3.5" fill="var(--border)" />
        <Marker x={194} y={92} r={20} />
      </Frame>
    ),
  },
  {
    text: "กด Install แล้วแอปจะเปิดในหน้าต่างของตัวเอง เปิดใช้งานครั้งต่อไปจากหน้าต่างนั้น",
    figure: (
      <Frame landscape>
        <rect x="30" y="40" width="200" height="98" rx="10" fill="var(--surface)" stroke="var(--border-strong)" strokeWidth="2" />
        <rect x="30" y="40" width="200" height="20" rx="10" fill="var(--surface-muted)" />
        <circle cx="44" cy="50" r="3.5" fill="var(--border-strong)" />
        <rect x="56" y="46" width="60" height="7" rx="3.5" fill="var(--border)" />
        <rect x="46" y="76" width="52" height="14" rx="7" fill="var(--brand)" />
        <Row y={104} w={140} muted />
        <Marker x={130} y={89} r={40} />
      </Frame>
    ),
  },
];

const PLATFORMS: { id: PlatformId; label: string; steps: Step[] }[] = [
  { id: "ios", label: "iPhone / iPad", steps: iosSteps },
  { id: "android", label: "Android", steps: androidSteps },
  { id: "desktop", label: "คอมพิวเตอร์", steps: desktopSteps },
];

/** Best guess only — the tabs are there because UA sniffing is never certain. */
function detectPlatform(): PlatformId {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac, so a touch-capable "Mac" is an iPad.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

/* --- dialog ------------------------------------------------------------- */

export default function InstallGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [platform, setPlatform] = useState<PlatformId>(detectPlatform);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setPlatform(detectPlatform());
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const steps = PLATFORMS.find((entry) => entry.id === platform)?.steps ?? iosSteps;

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  const selectPlatform = (id: PlatformId) => {
    setPlatform(id);
    setStep(0);
    trackRef.current?.scrollTo({ left: 0 });
  };

  return (
    <dialog ref={dialogRef} className="guide" onClose={onClose} onCancel={onClose}>
      <div className="guide__head">
        <div>
          <h2 className="guide__title">วิธีติดตั้งเป็นแอป</h2>
          <p className="guide__sub">ปัดซ้าย-ขวาเพื่อดูทีละขั้น</p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="ปิด">
          <CloseIcon size={18} />
        </button>
      </div>

      <div className="guide__tabs" role="tablist" aria-label="เลือกอุปกรณ์">
        {PLATFORMS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={platform === id}
            className="guide__tab"
            onClick={() => selectPlatform(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        className="guide__track"
        ref={trackRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          setStep(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)));
        }}
      >
        {steps.map((entry, index) => (
          <section className="guide__slide" key={`${platform}-${index}`} aria-label={`ขั้นที่ ${index + 1} จาก ${steps.length}`}>
            <div className="guide__figure">{entry.figure}</div>
            <p className="guide__step">
              <span className="guide__badge">{index + 1}</span>
              {entry.text}
            </p>
          </section>
        ))}
      </div>

      <div className="guide__nav">
        <button type="button" className="btn btn--icon" onClick={() => goTo(step - 1)} disabled={step === 0} aria-label="ขั้นก่อนหน้า">
          <ChevronLeftIcon size={18} />
        </button>
        <div className="guide__dots">
          {steps.map((_, index) => (
            <button
              key={index}
              type="button"
              className="guide__dot"
              aria-current={index === step ? "step" : undefined}
              aria-label={`ไปขั้นที่ ${index + 1}`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
        {step === steps.length - 1 ? (
          <button type="button" className="btn btn--icon" onClick={onClose} aria-label="เสร็จแล้ว">
            <CheckIcon size={18} />
          </button>
        ) : (
          <button type="button" className="btn btn--icon" onClick={() => goTo(step + 1)} aria-label="ขั้นถัดไป">
            <ChevronRightIcon size={18} />
          </button>
        )}
      </div>
    </dialog>
  );
}
